/**
 * XRLayerManager — creates and manages XR compositor layers.
 *
 * Supports XRProjectionLayer, XRQuadLayer, XREquirectLayer.
 * Graceful fallback when XRWebGLBinding is not available.
 */

import { Logger } from '../../core/Logger.js';

export interface QuadLayerInit {
  width: number;
  height: number;
  viewPixelWidth: number;
  viewPixelHeight: number;
  space: XRReferenceSpace;
  transform?: XRRigidTransform;
}

export interface EquirectLayerInit {
  space: XRReferenceSpace;
  viewPixelWidth: number;
  viewPixelHeight: number;
  centralHorizontalAngle?: number;
  upperVerticalAngle?: number;
  lowerVerticalAngle?: number;
}

export class XRLayerManager {
  private glBinding: any = null; // XRWebGLBinding
  private layers: XRLayer[] = [];
  private projectionLayer: XRProjectionLayer | null = null;
  private _available = false;

  /**
   * Initialize with an XR session and GL context.
   */
  init(session: XRSession, gl: WebGLRenderingContext | WebGL2RenderingContext): boolean {
    try {
      const XRWebGLBinding = (globalThis as any).XRWebGLBinding;
      if (!XRWebGLBinding) {
        Logger.debug('XRLayers', 'XRWebGLBinding not available — using baseLayer');
        return false;
      }

      this.glBinding = new XRWebGLBinding(session, gl);
      this._available = true;
      Logger.info('XRLayers', 'XRWebGLBinding initialized');
      return true;
    } catch (err) {
      Logger.debug('XRLayers', `Failed to create XRWebGLBinding: ${err}`);
      return false;
    }
  }

  get available(): boolean {
    return this._available;
  }

  /**
   * Create the main projection layer.
   */
  createProjectionLayer(init?: Partial<XRProjectionLayerInit>): XRProjectionLayer | null {
    if (!this.glBinding) return null;

    try {
      const proj = this.glBinding.createProjectionLayer({
        textureType: 'texture',
        colorFormat: 0x1908, // GL_RGBA
        depthFormat: 0x88F0, // GL_DEPTH24_STENCIL8
        scaleFactor: 1.0,
        ...init,
      });
      // Older browsers with partial WebXR layer support can return
      // null/undefined here. Without the explicit guard, the previous
      // non-null assertion `this.projectionLayer!` would push null into
      // `this.layers[]`, and dispose() would silently leak the compositor
      // resource (optional-chained `.destroy?.()` doesn't throw on null).
      if (!proj) {
        Logger.warn('XRLayers', 'createProjectionLayer returned null — backend may not support projection layers');
        return null;
      }
      this.projectionLayer = proj;
      this.layers.push(proj);
      Logger.info('XRLayers', 'Projection layer created');
      return proj;
    } catch (err) {
      Logger.warn('XRLayers', `Failed to create projection layer: ${err}`);
      return null;
    }
  }

  /**
   * Create a quad layer (for UI panels in VR).
   */
  createQuadLayer(init: QuadLayerInit): XRQuadLayer | null {
    if (!this.glBinding) return null;

    try {
      const layer = this.glBinding.createQuadLayer({
        space: init.space,
        viewPixelWidth: init.viewPixelWidth,
        viewPixelHeight: init.viewPixelHeight,
        width: init.width,
        height: init.height,
        transform: init.transform,
        layout: 'mono',
      });
      // Guard browsers whose binding lacks quad-layer support and return null:
      // a null pushed into this.layers later breaks updateRenderState/dispose.
      if (!layer) {
        Logger.warn('XRLayers', 'Quad layer not supported by this XRWebGLBinding');
        return null;
      }
      this.layers.push(layer);
      Logger.info('XRLayers', 'Quad layer created');
      return layer;
    } catch (err) {
      Logger.warn('XRLayers', `Failed to create quad layer: ${err}`);
      return null;
    }
  }

  /**
   * Create an equirect layer (for 360 backgrounds).
   */
  createEquirectLayer(init: EquirectLayerInit): any | null {
    if (!this.glBinding) return null;

    try {
      const layer = this.glBinding.createEquirectLayer({
        space: init.space,
        viewPixelWidth: init.viewPixelWidth,
        viewPixelHeight: init.viewPixelHeight,
        centralHorizontalAngle: init.centralHorizontalAngle ?? Math.PI * 2,
        upperVerticalAngle: init.upperVerticalAngle ?? Math.PI / 2,
        lowerVerticalAngle: init.lowerVerticalAngle ?? -Math.PI / 2,
        layout: 'mono',
      });
      if (!layer) {
        Logger.warn('XRLayers', 'Equirect layer not supported by this XRWebGLBinding');
        return null;
      }
      this.layers.push(layer);
      Logger.info('XRLayers', 'Equirect layer created');
      return layer;
    } catch (err) {
      Logger.warn('XRLayers', `Failed to create equirect layer: ${err}`);
      return null;
    }
  }

  /**
   * Get all layers for session.updateRenderState().
   */
  getLayers(): XRLayer[] {
    return this.layers;
  }

  /**
   * Get the projection layer.
   */
  getProjectionLayer(): XRProjectionLayer | null {
    return this.projectionLayer;
  }

  /**
   * Update per-frame layer state.
   */
  updateLayers(_frame: XRFrame): void {
    // Quad layer and equirect layer updates can be done here
    // (e.g., writing to sub-image textures via glBinding.getSubImage)
  }

  dispose(): void {
    // Call destroy() on every layer before releasing references so the
    // compositor reclaims GPU swapchain resources. Without this, repeated
    // XR session cycles leak compositor layers on Quest/VisionOS until the
    // page is reloaded (spec: XRCompositionLayer.destroy()).
    for (const layer of this.layers) {
      try {
        (layer as { destroy?: () => void }).destroy?.();
      } catch (err) {
        Logger.debug('XRLayers', `layer.destroy() threw: ${err}`);
      }
    }
    this.layers.length = 0;
    this.projectionLayer = null;
    this.glBinding = null;
    this._available = false;
  }
}
